import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import VideasAppLayout from '../../layouts/VideasAppLayout.vue'
import { analyticsRoutes } from '../../apps/analytics/router'
import { appealsRoutes } from '../../apps/appeals/router'
import { assetsRoutes } from '../../apps/assets/router'
import { subtitlesRoutes } from '../../apps/subtitles/router'
import { clipsRoutes } from '../../apps/clips/router'
import { websitesRoutes } from '../../apps/websites/router'
import { toolsRoutes } from '../../apps/tools/router'
import { sessionRoutes } from '../../apps/session/router'

// Debug routes are only available in dev mode (excluded from production build)
const debugRoutes: RouteRecordRaw[] = import.meta.env.DEV
  ? [
      {
        path: 'debug',
        name: 'debug',
        component: () => import('../../apps/debug/views/DebugIndexView.vue'),
        meta: { title: 'Debug', requiresAuth: true },
      },
      {
        path: 'debug/modals',
        name: 'debug-modals',
        component: () => import('../../apps/debug/views/DebugModalsView.vue'),
        meta: { title: 'Debug - Modals', requiresAuth: true },
      },
      {
        path: 'debug/tools',
        name: 'debug-tools',
        component: () => import('../../apps/debug/views/DebugToolsView.vue'),
        meta: { title: 'Debug - Tools', requiresAuth: true },
      },
    ]
  : []

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: { name: 'organizations:list' },
  },
  // Signing in, and the organization picker a sign-in lands on.
  ...sessionRoutes,
  // Public tools routes (no auth required)
  ...toolsRoutes,
  // DSA Art. 20 appeal flow — account-level, no org context (linked from
  // the Statement of Reasons email).
  ...appealsRoutes,
  {
    path: '/org/:organizationId',
    name: 'app:layout',
    component: VideasAppLayout,
    // Every screen of an organization needs a session, and the session guard only sets
    // `X-Org-Id` from the URL for a route that requires one. Declared once here because a
    // route's `meta` is merged from its parents: a child that forgot it would otherwise call
    // the organization's routes with no tenant and be refused.
    meta: { requiresAuth: true },
    children: [
      {
        path: 'home',
        name: 'home',
        component: () => import('../../apps/home/views/HomeView.vue'),
        meta: { title: 'Home', requiresAuth: true },
      },
      ...debugRoutes,
      ...assetsRoutes,
      ...subtitlesRoutes,
      ...clipsRoutes,
      ...websitesRoutes,
      ...analyticsRoutes,
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'core:not-found',
    component: () => import('../../apps/core/views/NotFoundView.vue'),
    meta: {
      title: 'Page Not Found',
      requiresAuth: false,
    },
  },
]

const router = createRouter({
  history: createWebHistory('/'),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

router.beforeEach((to, _from, next) => {
  document.title = `${to.meta.title || 'Videas'} - Videas`
  next()
})

export default router
